import { useState, useCallback, useEffect } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { APIError, useAxios } from "../core/_axios";
import LoadingSpinner from "./LoadingSpinner";
import dayjs from "dayjs";

import weekOfYear from "dayjs/plugin/weekOfYear";
import isoWeek from "dayjs/plugin/isoWeek";
import weekday from "dayjs/plugin/weekday";
import dayOfYear from "dayjs/plugin/dayOfYear";

dayjs.extend(weekOfYear);
dayjs.extend(isoWeek);
dayjs.extend(weekday);
dayjs.extend(dayOfYear);

type GroupBy = "" | "week" | "month" | "year";

function groupData(
    data: { date: string; count: number }[],
    groupBy: GroupBy
): { date: string; count: number }[] {
    if (!groupBy) return data;

    const grouped: Record<string, number> = {};

    data.forEach(({ date, count }) => {
        let key = date;
        if (groupBy === "week") {
            key = dayjs(date).startOf("week").format("YYYY-MM-DD");
        } else if (groupBy === "month") {
            key = dayjs(date).format("YYYY-MM");
        } else if (groupBy === "year") {
            key = dayjs(date).format("YYYY");
        }
        grouped[key] = (grouped[key] || 0) + count;
    });

    return Object.entries(grouped)
        .map(([date, count]) => ({ date, count }))
        .sort((a, b) => a.date.localeCompare(b.date));
}

export function UsersGraph() {
    const axios = useAxios();

    const [loading, setLoading] = useState(true);
    const [loadingError, setLoadingError] = useState<string | null>(null);
    const [data, setData] = useState<{ date: string, count: number }[]>([]);
    const [groupBy, setGroupBy] = useState<GroupBy>("month");

    const loadUsersData = useCallback(async () => {
        setLoadingError(null);
        setLoading(true);
        try {
            // Fetch users data - returns users_with_memberships
            const response = await axios.get('/api/v1/app/authenticated/management/users');
            const usersWithMemberships = response.data?.data?.users_with_memberships || [];
            
            console.log('Fetched users:', usersWithMemberships.length, 'users');
            console.log('Sample user data structure:', usersWithMemberships[0]);
            
            // Group users by creation date - each item has a "user" property with the actual user data
            const usersByDate: Record<string, number> = {};
            if (Array.isArray(usersWithMemberships)) {
                usersWithMemberships.forEach((item: any) => {
                    const userData = item?.user || item; // Handle both nested and flat structures
                    if (userData?.inserted_at) {
                        const date = dayjs(userData.inserted_at).format('YYYY-MM-DD');
                        usersByDate[date] = (usersByDate[date] || 0) + 1;
                    }
                });
            }
            
            // Convert to array format for incremental counts (not cumulative)
            const formatted = Object.entries(usersByDate)
                .map(([date, count]) => ({ date, count }))
                .sort((a, b) => a.date.localeCompare(b.date));
            
            console.log('Formatted user data:', formatted);
            setData(formatted);
        } catch (exception) {
            console.error(exception);
            const errorMessage =
                exception instanceof APIError
                    ? exception.message
                    : "An unknown error has occurred. Please try again.";
            setLoadingError(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [axios]);

    useEffect(() => {
        loadUsersData();
    }, [loadUsersData]);

    if (loading) {
        return <LoadingSpinner />;
    }

    if (loadingError) {
        return (
            <div>
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                    <span className="block sm:inline">{loadingError}</span>
                </div>
                <div className="mt-5">
                    <button className="btn btn-lg btn-light" onClick={() => {
                        setLoadingError(null);
                        loadUsersData();
                    }}>
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    // Fill in missing periods with zeros and add projections
    const fillMissingPeriods = (data: { date: string; count: number }[]): { date: string; count: number; actualCount?: number; isProjected?: boolean }[] => {
        if (data.length === 0) return data;
        
        const result: { date: string; count: number; actualCount?: number; isProjected?: boolean }[] = [];
        
        if (groupBy === "month") {
            const monthMap = new Map(data.map(d => [d.date, d.count]));
            
            // Find the earliest and latest dates
            const dates = data.map(d => d.date).sort();
            const startDate = dayjs(dates[0]);
            const currentMonth = dayjs().format('YYYY-MM');
            const endDate = dayjs(currentMonth);
            
            // Generate all months from start to current
            let current = startDate;
            while (current.isBefore(endDate) || current.format('YYYY-MM') === currentMonth) {
                const monthKey = current.format('YYYY-MM');
                const count = monthMap.get(monthKey) || 0;
                
                if (monthKey === currentMonth && count > 0) {
                    // For current month, show projected value with actual as metadata
                    const dayOfMonth = dayjs().date();
                    const daysInMonth = dayjs().daysInMonth();
                    const projectedCount = Math.round((count / dayOfMonth) * daysInMonth);
                    
                    if (projectedCount > count) {
                        result.push({ 
                            date: monthKey, 
                            count: projectedCount, 
                            actualCount: count, 
                            isProjected: true 
                        });
                    } else {
                        result.push({ date: monthKey, count });
                    }
                } else {
                    result.push({ date: monthKey, count });
                }
                
                current = current.add(1, 'month');
            }
        } else if (groupBy === "year") {
            const yearMap = new Map(data.map(d => [d.date, d.count]));
            
            // Find the earliest and latest years
            const years = data.map(d => d.date).sort();
            const startYear = years[0];
            const currentYear = dayjs().format('YYYY');
            
            // Generate all years from start to current
            for (let year = parseInt(startYear); year <= parseInt(currentYear); year++) {
                const yearKey = year.toString();
                const count = yearMap.get(yearKey) || 0;
                
                if (yearKey === currentYear && count > 0) {
                    // For current year, show projected value with actual as metadata
                    const dayOfYear = dayjs().dayOfYear();
                    const daysInYear = dayjs().endOf('year').dayOfYear();
                    const projectedCount = Math.round((count / dayOfYear) * daysInYear);
                    
                    if (projectedCount > count) {
                        result.push({ 
                            date: yearKey, 
                            count: projectedCount, 
                            actualCount: count, 
                            isProjected: true 
                        });
                    } else {
                        result.push({ date: yearKey, count });
                    }
                } else {
                    result.push({ date: yearKey, count });
                }
            }
        } else {
            return data;
        }
        
        return result;
    };
    
    const groupedData = fillMissingPeriods(groupData(data, groupBy));

    // Get current period data for display
    const currentPeriodData = groupedData[groupedData.length - 1];
    const hasProjection = currentPeriodData?.isProjected;
    const periodLabel = groupBy === "year" ? "Year" : "Month";

    return (
        <div className="grid gap-5 lg:gap-7.5">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-semibold">Users Over Time</h2>
                    {hasProjection && currentPeriodData && (
                        <p className="text-sm text-gray-600 mt-1">
                            <span className="text-amber-600">Current {periodLabel}: {currentPeriodData.actualCount}</span>
                            {' • '}
                            <span className="text-gray-500">Projected: {currentPeriodData.count}</span>
                        </p>
                    )}
                </div>
                <div className="flex gap-2">
                    <button
                        className={`px-3 py-1 rounded ${groupBy === '' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                        onClick={() => setGroupBy('')}
                    >
                        Day
                    </button>
                    <button
                        className={`px-3 py-1 rounded ${groupBy === 'week' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                        onClick={() => setGroupBy('week')}
                    >
                        Week
                    </button>
                    <button
                        className={`px-3 py-1 rounded ${groupBy === 'month' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                        onClick={() => setGroupBy('month')}
                    >
                        Month
                    </button>
                    <button
                        className={`px-3 py-1 rounded ${groupBy === 'year' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                        onClick={() => setGroupBy('year')}
                    >
                        Year
                    </button>
                </div>
            </div>
            <ResponsiveContainer width="100%" height={400}>
                <LineChart data={groupedData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                        dataKey="date" 
                        interval={groupBy === "month" ? 0 : "preserveStartEnd"}
                        angle={groupBy === "month" && groupedData.length > 12 ? -45 : 0}
                        textAnchor={groupBy === "month" && groupedData.length > 12 ? "end" : "middle"}
                        height={groupBy === "month" && groupedData.length > 12 ? 60 : undefined}
                    />
                    <YAxis allowDecimals={false} />
                    <Tooltip 
                        content={({ active, payload }) => {
                            if (active && payload && payload.length > 0) {
                                const data = payload[0].payload;
                                return (
                                    <div className="bg-white p-2 border border-gray-300 rounded shadow">
                                        <p className="font-semibold">{data.date}</p>
                                        {data.isProjected ? (
                                            <>
                                                <p className="text-amber-600">Current: {data.actualCount}</p>
                                                <p className="text-gray-500">Projected: {data.count}</p>
                                            </>
                                        ) : (
                                            <p>Count: {data.count}</p>
                                        )}
                                    </div>
                                );
                            }
                            return null;
                        }}
                    />
                    <Line 
                        type="monotone" 
                        dataKey="count" 
                        stroke="#3b82f6" 
                        strokeWidth={2}
                        dot={(props: any) => {
                            const { cx, cy, payload, index } = props;
                            if (payload.isProjected) {
                                return <circle key={`dot-projected-${index}`} cx={cx} cy={cy} r={4} fill="#9ca3af" />;
                            }
                            return <circle key={`dot-${index}`} cx={cx} cy={cy} r={3} fill="#3b82f6" />;
                        }}
                    />
                    {/* Add actual value dots for current month */}
                    <Line 
                        type="monotone" 
                        dataKey="actualCount" 
                        stroke="transparent" 
                        strokeWidth={0}
                        dot={(props: any) => {
                            const { cx, cy, payload, index } = props;
                            if (payload.actualCount) {
                                return <circle key={`dot-actual-${index}`} cx={cx} cy={cy} r={5} fill="#fbbf24" stroke="#f59e0b" strokeWidth={1} />;
                            }
                            return null;
                        }}
                        connectNulls={false}
                        isAnimationActive={false}
                    />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
}